import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Space, Button, Row, Col, Card, Popconfirm, Image, Tooltip, message } from "antd";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import { useTranslation } from "react-i18next";
import { ViewpointManager } from "./ViewpointManager";
import { placeholderPath } from "utils/placeholderPath";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { useEffect, useRef, useState } from "react";
import { resolveViewpointModel, hasAnyViewableModel } from "Components/XKTViewer/utils/resolveViewpointModel";

interface BcfIssueViewpointsProps {
  viewpointManager: ViewpointManager;
  onEnableViewpointAddMode?: () => void;
  addEnabled?: boolean;
  addDelete?: boolean;
  showViewInBimViewer?: boolean;
  issueModels?: API_BcfServerIfcFileInfo[];
  selectedIssueGuid?: string;
  showDeletedTopics?: boolean;
  viewpoints?: API_BcfViewpoint[] | API_BcfServerViewpoint[];
  removeViewpoint?: (topicGuid: string, viewpointGuid: string) => API_BcfViewpoint[] | API_BcfServerViewpoint[];
  comments?: API_BcfServerComment[] | API_BcfConflictComment[];
  setConfirmOnCancel?: (b: boolean) => void;
}

export const BcfIssueViewpoints = observer(
  ({
    viewpointManager,
    onEnableViewpointAddMode,
    addEnabled = true,
    showViewInBimViewer,
    addDelete = true,
    issueModels,
    selectedIssueGuid,
    showDeletedTopics,
    viewpoints,
    removeViewpoint,
    comments,
    setConfirmOnCancel,
  }: BcfIssueViewpointsProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [bcfViewpoints, setBcfViewpoints] = useState<API_BcfViewpoint[] | API_BcfServerViewpoint[]>();
    useEffect(() => {
      if (viewpoints?.length) {
        setBcfViewpoints(viewpoints);
      }
    }, []);

    const setConfirmOnCancelRef = useRef(setConfirmOnCancel);
    setConfirmOnCancelRef.current = setConfirmOnCancel;

    useEffect(() => {
      return reaction(
        () => viewpointManager.hasPendingChanges,
        (hasPending) => {
          if (hasPending) {
            setConfirmOnCancelRef.current?.(true);
          }
        },
        { fireImmediately: true }
      );
    }, [viewpointManager]);

    const fromBimModule = issueModels ? "bcf_viewpoints" : "viewer_viewpoints";

    const viewViewpoint = async (viewpoint: API_BcfServerViewpoint, issueModels: API_BcfServerIfcFileInfo[]) => {
      const targetModel = resolveViewpointModel(issueModels);
      if (!targetModel) return;

      await xktViewerStore.initViewer(
        {
          fileName: targetModel.fileName,
          fileId: targetModel.id,
          initialViewpoint: {
            viewpoint: viewpoint,
            topicId: viewpointManager.topicId,
            fileId: targetModel.id,
          },
        },
        issueModels,
        selectedIssueGuid,
        showDeletedTopics
      );
    };

    const isViewpointUsedInComments = (viewpointGuid: string): boolean => {
      if (!comments || comments.length === 0) {
        return false;
      }
      return comments.some(
        (comment: API_BcfServerComment | API_BcfConflictComment) =>
          ("viewpoint_guid" in comment ? comment.viewpoint_guid : (comment as API_BcfConflictComment).viewpointGuid) ===
          viewpointGuid
      );
    };

    const removeViewpointFromTopic = (topicGuid: string, viewpointGuid: string) => {
      if (isViewpointUsedInComments(viewpointGuid)) {
        message.warning(t("Cannot remove viewpoint that is used in comments"));
        return;
      }

      if (viewpoints?.length) {
        const updatedList = removeViewpoint(topicGuid, viewpointGuid);
        setBcfViewpoints(updatedList);
      } else {
        viewpointManager.remove(viewpointGuid);
      }
      setConfirmOnCancel?.(true);
    };

    const renderViewpointsList = (bcfViewpoints?.length ? bcfViewpoints : viewpointManager.viewpoints).map((item) => {
      const isUsedInComments = isViewpointUsedInComments(item.guid);

      return (
        <Col sm={8} md={6} key={item.guid} className="mb-2 relative">
          <Card>
            <div className="viewpoint-actions">
              {showViewInBimViewer && issueModels.length !== 0 && (
                <Tooltip
                  title={
                    hasAnyViewableModel(issueModels) ? t("View with BIM Viewer") : t("BIM Viewer (being processed)")
                  }
                  placement="bottom"
                >
                  <div
                    className="mr-2"
                    onClick={() => hasAnyViewableModel(issueModels) && viewViewpoint(item, issueModels)}
                  >
                    <FontAwesomeIcon fixedWidth icon={["far", "bullseye-pointer"]} />
                  </div>
                </Tooltip>
              )}
              {addDelete &&
                (isUsedInComments ? (
                  <Tooltip title={t("Cannot remove viewpoint that is used in comments")} placement="bottom">
                    <div className="mr-2" style={{ cursor: "not-allowed", opacity: 0.5 }}>
                      <FontAwesomeIcon fixedWidth icon={["far", "trash-alt"]} />
                    </div>
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title={t("Are you sure?")}
                    okText={t("Yes")}
                    cancelText={t("No")}
                    onConfirm={() => removeViewpointFromTopic(selectedIssueGuid, item.guid)}
                    getPopupContainer={() => document.getElementById(fromBimModule)}
                  >
                    <Tooltip title={t("Remove")} placement="bottom">
                      <div className="mr-2">
                        <FontAwesomeIcon fixedWidth icon={["far", "trash-alt"]} />
                      </div>
                    </Tooltip>
                  </Popconfirm>
                ))}
            </div>
            <Image
              src={item.imageSrc || `data:image/${item.snapshot?.snapshot_type};base64,${item.snapshot?.snapshot_data}`}
              fallback={placeholderPath}
            />
          </Card>
        </Col>
      );
    });

    return (
      <div id={fromBimModule}>
        {addEnabled && (
          <Space size="small" className="mb-md">
            <Button onClick={onEnableViewpointAddMode}>
              <span className="fa-stack relative" style={{ top: -4, left: -2 }}>
                <FontAwesomeIcon fixedWidth icon={["far", "camera"]} className="fa-stack" />
                <FontAwesomeIcon
                  fixedWidth
                  icon={["fas", "plus"]}
                  className="fa-stack-1x"
                  transform="right-16 down-5 shrink-4"
                  // enableBackground={"#fff"}
                />
              </span>
            </Button>
          </Space>
        )}
        <Row gutter={10}>{renderViewpointsList}</Row>
      </div>
    );
  }
);
